/**
 * @file use-pointer-reorder-list.ts
 *
 * Pointer-driven row reorder with Figma-aligned affordances: drag from a handle
 * only, ghost the source row at partial opacity, an insertion indicator, and a
 * cursor-following floating preview. Reusable for any bordered row list.
 * Ported from the design-reviewed Website Builder v2 implementation.
 *
 * {@link computeReorderedIndexes} maps "insert before" onto a splice index once
 * the dragged row is removed from its slot.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent } from 'react';

export type DragPreviewPosition = { x: number; y: number } | null;

export type PointerReorderListControls = {
  activeIndex: number | null;
  /** Visual insertion slot index in `0`…`length` (`length` means after the last row). */
  insertBeforeIndex: number | null;
  previewPosition: DragPreviewPosition;
  bindRowRef: (index: number) => (el: HTMLTableRowElement | null) => void;
  bindDragHandleProps: (
    index: number,
    options?: { disabled?: boolean },
  ) => {
    tabIndex: number;
    onPointerDown: (e: PointerEvent<HTMLElement>) => void;
  };
  isDragging: (index: number) => boolean;
  insertLineBeforeIndex: (index: number) => boolean;
  /** Insertion line after the last row when the slot is past the end. */
  insertLineAfterLastRow: (index: number) => boolean;
};

/**
 * Maps "insert-before" semantics (among `length` rendered rows during a drag)
 * onto a splice index after the dragged row is removed.
 *
 * @param listLength - The number of rows in the list.
 * @param fromIndex - The index the row is dragged from.
 * @param insertBefore - The visual insertion slot (`0`…`length`).
 *
 * @returns The splice target index for the removed row.
 */
export function computeReorderedIndexes(
  listLength: number,
  fromIndex: number,
  insertBefore: number,
): number {
  let insert = Math.min(insertBefore, listLength);
  if (insert < 0) insert = 0;
  insert = insert <= fromIndex ? insert : insert - 1;
  return insert;
}

function insertionIndexFromPointerY(clientY: number, rowRects: (DOMRect | null)[]): number {
  for (let i = 0; i < rowRects.length; i++) {
    const rect = rowRects[i];
    if (!rect || rect.height === 0) continue;
    if (clientY < rect.top + rect.height / 2) return i;
  }
  return rowRects.length;
}

function getRowRects(rows: (HTMLTableRowElement | null)[]): (DOMRect | null)[] {
  return rows.map((row) => row?.getBoundingClientRect() ?? null);
}

/**
 * Provides controlled pointer-drag reordering for a list of table rows.
 *
 * @param items - The current ordered items.
 * @param onReorder - Called with the next ordered array when a drag commits.
 *
 * @returns Bindings for the rows and drag handles plus the active drag state.
 */
export function usePointerReorderList<T>(
  items: readonly T[],
  onReorder: (next: T[]) => void,
): PointerReorderListControls {
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const onReorderRef = useRef(onReorder);
  onReorderRef.current = onReorder;

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [insertBeforeIndex, setInsertBeforeIndex] = useState<number | null>(null);
  const [previewPosition, setPreviewPosition] = useState<DragPreviewPosition>(null);

  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  const dragFromRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    rowRefs.current = rowRefs.current.slice(0, items.length);
  }, [items.length]);

  const bindRowRef = useCallback(
    (index: number) => (el: HTMLTableRowElement | null) => {
      rowRefs.current[index] = el;
    },
    [],
  );

  /** Aborts active drag listeners and resets all drag state. */
  const resetDrag = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    dragFromRef.current = null;
    setActiveIndex(null);
    setInsertBeforeIndex(null);
    setPreviewPosition(null);
    document.body.style.userSelect = '';
  }, []);

  /** Completes or cancels a drag, optionally committing the reorder. */
  const finishDrag = useCallback(
    (clientY: number | undefined, cancelled: boolean) => {
      const from = dragFromRef.current;
      const list = itemsRef.current;
      resetDrag();

      if (cancelled || from === null || clientY === undefined || list.length <= 1) return;

      const insert = insertionIndexFromPointerY(clientY, getRowRects(rowRefs.current));
      if (insert === from) return;

      const target = computeReorderedIndexes(list.length, from, insert);
      if (target === from) return;

      const next = [...list];
      const [moved] = next.splice(from, 1);
      next.splice(target, 0, moved);
      onReorderRef.current(next);
    },
    [resetDrag],
  );

  const bindDragHandleProps = useCallback(
    (index: number, opts?: { disabled?: boolean }) => ({
      tabIndex: opts?.disabled || itemsRef.current.length < 2 ? -1 : 0,
      onPointerDown: (e: PointerEvent<HTMLElement>) => {
        if (opts?.disabled || itemsRef.current.length < 2 || e.button > 0) return;
        e.preventDefault();

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          /* noop */
        }

        dragFromRef.current = index;
        setActiveIndex(index);
        setPreviewPosition({ x: e.clientX, y: e.clientY });
        setInsertBeforeIndex(insertionIndexFromPointerY(e.clientY, getRowRects(rowRefs.current)));
        document.body.style.userSelect = 'none';

        const listenerOptions = { signal: controller.signal, capture: true } as const;

        document.addEventListener(
          'pointermove',
          (evt) => {
            setPreviewPosition({ x: evt.clientX, y: evt.clientY });
            setInsertBeforeIndex(insertionIndexFromPointerY(evt.clientY, getRowRects(rowRefs.current)));
          },
          listenerOptions,
        );

        document.addEventListener(
          'pointerup',
          (evt) => {
            finishDrag(evt.clientY, false);
          },
          listenerOptions,
        );

        document.addEventListener(
          'pointercancel',
          () => {
            finishDrag(undefined, true);
          },
          listenerOptions,
        );
      },
    }),
    [finishDrag],
  );

  useEffect(() => resetDrag, [resetDrag]);

  const isDragging = useCallback((index: number) => activeIndex === index, [activeIndex]);

  const insertLineBeforeIndex = useCallback(
    (index: number) => insertBeforeIndex === index && activeIndex !== null,
    [insertBeforeIndex, activeIndex],
  );

  const insertLineAfterLastRow = useCallback(
    (index: number) =>
      activeIndex !== null &&
      insertBeforeIndex === itemsRef.current.length &&
      index === itemsRef.current.length - 1 &&
      itemsRef.current.length > 0,
    [insertBeforeIndex, activeIndex],
  );

  return {
    activeIndex,
    insertBeforeIndex,
    previewPosition,
    bindRowRef,
    bindDragHandleProps,
    isDragging,
    insertLineBeforeIndex,
    insertLineAfterLastRow,
  };
}
